'use client';

import { Typography } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';

import { useBackupPolicySupportedProperties } from '@/app/(dashboard)/settings/backup-policies/backupPolicyProperties';
import { ResourcesSelectionEditor } from '@/components/resourcesSelection/resourcesSelectionEditor';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { hasValidConditionValues } from '@/types/advanceFilter';

import type { ZeroEtlPolicyEditState } from './ZeroEtlPolicyCreationFlow';
import { ZeroEtlDisclaimer } from './zeroEtlDisclaimer';
import { ZERO_ETL_SUPPORTED_RESOURCE_TYPES } from './zeroEtlPolicyTypes';

export const ZeroEtlPolicyConditionEditor = (
  props: StepProps<ZeroEtlPolicyEditState>
) => {
  const supportedProperties = useBackupPolicySupportedProperties(
    ZERO_ETL_SUPPORTED_RESOURCE_TYPES,
    [Provider.Aws]
  );
  const nextButtonDisabledTooltip =
    props.wizardState.resourcesScopeType === 'useConditions' &&
    !hasValidConditionValues(props.wizardState.condition)
      ? 'Please create a condition with all required fields'
      : undefined;

  return (
    <StepContainer
      sx={{ padding: '0px' }}
      stepperLabels={props.stepperLabels}
      stepperIndex={0}
      nextButtonDisabledTooltip={nextButtonDisabledTooltip}
      canGoNext={() =>
        props.wizardState.resourcesScopeType !== 'useConditions' ||
        hasValidConditionValues(props.wizardState.condition)
      }
      onNextClick={async () => {
        props.setNextStep(props.currentStep.next?.[0]);
      }}
      navigationComponent={
        <RenameEntityName
          focusOnMount={props.wizardState.focusOnName}
          label='Integration name'
          name={props.wizardState.name}
          onSubmit={(name) =>
            props.setWizardState((state) => ({ ...state, name }))
          }
        />
      }
    >
      <ZeroEtlDisclaimer />
      <ResourcesSelectionEditor
        supportedProviders={[Provider.Aws]}
        supportedResourceTypes={ZERO_ETL_SUPPORTED_RESOURCE_TYPES}
        condition={props.wizardState.condition}
        setCondition={(condition) =>
          props.setWizardState({ ...props.wizardState, condition })
        }
        includedResources={props.wizardState.include}
        excludedResources={props.wizardState.exclude}
        setIncludedResources={(includedResources) =>
          props.setWizardState({
            ...props.wizardState,
            include: includedResources,
          })
        }
        setExcludedResources={(excludedResources) =>
          props.setWizardState({
            ...props.wizardState,
            exclude: excludedResources,
          })
        }
        resourcesScopeType={props.wizardState.resourcesScopeType}
        setResourcesScopeType={(resourcesScopeType) =>
          props.setWizardState({
            ...props.wizardState,
            resourcesScopeType,
          })
        }
        supportedProperties={supportedProperties}
        conditionTitle={
          <Typography variant='subtitle1' sx={{ mb: '24px' }}>
            Include resources based on these conditions:
          </Typography>
        }
        allExcludedDescription="This Zero-ETL policy won't be automatically applied to any resources. Only manually included resources will be assigned to the policy."
        allIncludedDescription="This Zero-ETL policy will be automatically applied to all resources of the supported types. When new ones are discovered, they'll be included too."
        manualOverridesDescription='Manually include or exclude resources from your Zero-ETL policy, regardless of dynamic selection.'
      />
    </StepContainer>
  );
};
